import { __awaiter, __rest } from "tslib";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { EDGE_PRIMARY_COLOR, fixElementsCenter } from '../index';
import ReactFlow, { Background, useEdgesState, useNodesState, updateEdge, getBezierPath, } from 'reactflow';
import { generateOFlowCanvasId } from '../../utils';
import { useDndDrop, useOFlowModel } from '../../hooks';
import { dataTransForm } from './utils';
import 'reactflow/dist/style.css';
import './index.less';
import { merge } from 'lodash';
/**
 *  按住ctrl/command时，点击节点，进行多选
 *
 */
const keyboardStatusMapping = {
    ctrlKey: false,
    metaKey: false,
};
window.addEventListener('keydown', event => {
    const { ctrlKey, metaKey } = event;
    keyboardStatusMapping.ctrlKey = ctrlKey;
    keyboardStatusMapping.metaKey = metaKey;
});
window.addEventListener('keyup', event => {
    const { ctrlKey, metaKey } = event;
    keyboardStatusMapping.ctrlKey = ctrlKey;
    keyboardStatusMapping.metaKey = metaKey;
});
const CanvasRenderContainer = forwardRef((props, ref) => {
    const { dropRef } = useDndDrop();
    const { canvasId, children } = props;
    return (React.createElement("div", { id: `oflow-client--canvas-${canvasId}`, style: { height: '100%', flexGrow: 1 }, ref: dom => {
            // @ts-ignore
            ref.current = dom;
            dropRef(dom);
        } }, children));
});
const useTypes = ({ canvasId, canvasModel }) => {
    const model = useOFlowModel();
    const { nodeTypeMap, edgeTypeMap } = model;
    const [updateKey, setUpdateKey] = useState();
    const nodeTypes = useMemo(() => {
        return canvasModel
            ? Object.entries(nodeTypeMap).reduce((init, [type, node]) => {
                const { Render } = node;
                init[type] = props => {
                    const { id, data = {}, xPos: left, yPos: top } = props;
                    const instance = useMemo(() => {
                        const OFlowNodeClass = nodeTypeMap[type];
                        return new OFlowNodeClass({
                            canvasId,
                            config: Object.assign({ id, left, top, type }, data),
                            model: model,
                        });
                    }, []);
                    useEffect(() => {
                        instance === null || instance === void 0 ? void 0 : instance.setConfig(previous => {
                            merge(previous, { left, top });
                            return previous;
                        });
                    }, [left, top]);
                    return React.createElement(Render, { instance: instance });
                };
                return init;
            }, {})
            : {};
    }, [canvasModel, updateKey]);
    const edgeTypes = useMemo(() => {
        return canvasModel
            ? Object.entries(edgeTypeMap).reduce((init, [type, edge]) => {
                const { Render } = edge;
                init[type] = props => {
                    const instance = useMemo(() => {
                        const OFlowEdgeClass = edgeTypeMap[type];
                        const { animated } = props, rest = __rest(props, ["animated"]);
                        return new OFlowEdgeClass({
                            canvasId,
                            config: Object.assign(Object.assign({}, rest), { type }),
                            model: model,
                        });
                    }, []);
                    return React.createElement(Render, { instance: instance, shapeInfo: props });
                };
                return init;
            }, {})
            : {};
    }, [canvasModel, updateKey]);
    useEffect(() => {
        const fn = () => {
            setUpdateKey(Math.random());
        };
        model === null || model === void 0 ? void 0 : model.onRegisterTypeChange(fn);
        return () => {
            model === null || model === void 0 ? void 0 : model.offRegisterTypeChange(fn);
        };
    }, [model]);
    return { nodeTypes, edgeTypes };
};
const useInitCanvasModel = (props) => {
    const model = useOFlowModel();
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const editable = model === null || model === void 0 ? void 0 : model.getEditable();
    const { id: canvasId, autoFixCenter, canvasData, canvasDomRef, rankConfig, flowInstance } = props;
    const [canvasModel, setCanvasModel] = useState();
    const utilsRef = useRef({});
    utilsRef.current.flowInstance = flowInstance;
    const canvasInstance = useRef({
        canvasId,
        batchAddNodesByConfig: nodes => {
            setNodes(previous => {
                const reactFlowNodes = nodes === null || nodes === void 0 ? void 0 : nodes.map(dataTransForm.transformNodeConfig2ReactFlowNodeConfig);
                return [...previous, ...reactFlowNodes];
            });
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(true);
                }, 10);
            });
        },
        batchAddEdgesByConfig: edges => {
            setEdges(previous => {
                const reactFlowEdges = edges === null || edges === void 0 ? void 0 : edges.map(dataTransForm.transformEdgeConfig2ReactFlowEdgeConfig);
                return [...previous, ...reactFlowEdges];
            });
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(true);
                }, 10);
            });
        },
        batchDeleteNodesById: (ids) => {
            setNodes(previous => {
                return previous.filter(({ id }) => !ids.includes(id));
            });
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(true);
                }, 10);
            });
        },
        batchDeleteEdgesById: (ids) => {
            setEdges(previous => {
                return previous.filter(({ id }) => !ids.includes(id));
            });
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(true);
                }, 10);
            });
        },
        batchMoveNodes: positionsInfo => {
            setNodes(previousNodes => {
                positionsInfo === null || positionsInfo === void 0 ? void 0 : positionsInfo.forEach(({ id, left, top }) => {
                    const nodeData = previousNodes === null || previousNodes === void 0 ? void 0 : previousNodes.find(node => (node === null || node === void 0 ? void 0 : node.id) === id);
                    if (nodeData) {
                        nodeData.position = {
                            x: left,
                            y: top,
                        };
                    }
                });
                return [...previousNodes];
            });
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(true);
                }, 10);
            });
        },
        terminal2canvas: ([x, y]) => {
            var _a;
            const rect = (_a = canvasDomRef === null || canvasDomRef === void 0 ? void 0 : canvasDomRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const newX = x - rect.x;
            const newY = y - rect.y;
            const project = utilsRef.current.flowInstance.project({ x: newX, y: newY });
            return [project.x, project.y];
        },
        getZoom: () => {
            return utilsRef.current.flowInstance.getZoom();
        },
        zoom: zoomLevel => {
            utilsRef.current.flowInstance.zoomTo(zoomLevel);
        },
        focusCenterWithAnimate: () => {
            utilsRef.current.flowInstance.fitView({ maxZoom: 1 });
        },
        drawByConfig: (canvasData) => {
            const { nodes, edges } = canvasData;
            setNodes(nodes === null || nodes === void 0 ? void 0 : nodes.map(dataTransForm.transformNodeConfig2ReactFlowNodeConfig));
            setEdges(edges === null || edges === void 0 ? void 0 : edges.map(dataTransForm.transformEdgeConfig2ReactFlowEdgeConfig));
        },
        canvasDomRef,
    }).current;
    useEffect(() => {
        const initCanvasData = canvasData || { nodes: [], edges: [], groups: [] };
        const fixedCanvasData = autoFixCenter
            ? fixElementsCenter({
                canvasData: initCanvasData,
                oFlowModel: model,
                canvasDom: canvasDomRef === null || canvasDomRef === void 0 ? void 0 : canvasDomRef.current,
            })
            : initCanvasData;
        const canvasModel = model.buildCanvasModel({
            canvasId,
            canvas: canvasInstance,
            canvasData: fixedCanvasData,
            rankConfig,
        });
        setCanvasModel(canvasModel);
    }, []);
    useEffect(() => {
        if (canvasData) {
            const previousHooks = model.getCommandPreviousHooks('CANVAS_DATA_UPDATE');
            let newParams = Object.assign({}, props);
            for (const hookFn of previousHooks) {
                newParams = hookFn({ payload: newParams, model: canvasModel });
            }
            const fixedCanvasData = autoFixCenter
                ? fixElementsCenter({
                    canvasData,
                    oFlowModel: model,
                    canvasDom: canvasDomRef === null || canvasDomRef === void 0 ? void 0 : canvasDomRef.current,
                })
                : canvasData;
            const { nodes = [], edges } = fixedCanvasData;
            const reactFlowNodes = nodes === null || nodes === void 0 ? void 0 : nodes.map(dataTransForm.transformNodeConfig2ReactFlowNodeConfig);
            const reactFLowEdges = edges === null || edges === void 0 ? void 0 : edges.map(dataTransForm.transformEdgeConfig2ReactFlowEdgeConfig);
            setNodes(reactFlowNodes);
            setEdges(reactFLowEdges);
            setTimeout(() => {
                var _a, _b, _c;
                (_b = (_a = canvasModel === null || canvasModel === void 0 ? void 0 : canvasModel.commandHistoryModel) === null || _a === void 0 ? void 0 : _a.resetCommandHistory) === null || _b === void 0 ? void 0 : _b.call(_a);
                (_c = model.getCommandPostHooks('CANVAS_DATA_UPDATE')) === null || _c === void 0 ? void 0 : _c.forEach(hookFn => {
                    hookFn({
                        canvasModel,
                        payload: { canvasData, autoFixCenter },
                    });
                });
            }, 100);
        }
    }, [canvasData]);
    return {
        canvasModel,
        nodes,
        onNodesChange: editable ? onNodesChange : null,
        onEdgesChange,
        canvasInstance,
        setEdges,
        edges,
    };
};
const useBindEvents = ({ canvasModel, canvasDomRef, canvasInstance, }) => {
    useEffect(() => {
        var _a, _b;
        const mouseMove = e => {
            var _a, _b, _c, _d;
            const { clientX, clientY } = e;
            canvasInstance.mouseInCanvas = true;
            canvasInstance.mousePosition = { x: clientX, y: clientY };
            const elementPath = (e === null || e === void 0 ? void 0 : e.path) || ((_a = e === null || e === void 0 ? void 0 : e.composedPath) === null || _a === void 0 ? void 0 : _a.call(e)) || [];
            const oflowElemetId = (_d = (_c = (_b = Array.from(elementPath)) === null || _b === void 0 ? void 0 : _b.find((dom) => { var _a; return (_a = dom === null || dom === void 0 ? void 0 : dom.attributes) === null || _a === void 0 ? void 0 : _a['oflow-element-id']; })) === null || _c === void 0 ? void 0 : _c.attributes['oflow-element-id']) === null || _d === void 0 ? void 0 : _d.value;
            if (oflowElemetId) {
                canvasModel === null || canvasModel === void 0 ? void 0 : canvasModel.setHoveredElementId(oflowElemetId);
            }
            else {
                canvasModel === null || canvasModel === void 0 ? void 0 : canvasModel.setHoveredElementId();
            }
        };
        const mouseLeave = e => {
            canvasInstance.mouseInCanvas = false;
        };
        (_a = canvasDomRef === null || canvasDomRef === void 0 ? void 0 : canvasDomRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('mousemove', mouseMove);
        (_b = canvasDomRef === null || canvasDomRef === void 0 ? void 0 : canvasDomRef.current) === null || _b === void 0 ? void 0 : _b.addEventListener('mouseleave', mouseLeave);
        return () => {
            var _a, _b;
            (_a = canvasDomRef === null || canvasDomRef === void 0 ? void 0 : canvasDomRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('mousemove', mouseMove);
            (_b = canvasDomRef === null || canvasDomRef === void 0 ? void 0 : canvasDomRef.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mouseleave', mouseLeave);
        };
    }, [canvasModel]);
};
const defaultConnectionLine = ({ fromX, fromY, fromPosition, toX, toY, toPosition }) => {
    const [edgePath] = getBezierPath({
        sourceX: fromX,
        sourceY: fromY,
        targetX: toX,
        targetY: toY,
        targetPosition: toPosition,
        sourcePosition: fromPosition,
    });
    return (React.createElement("g", null,
        React.createElement("path", { fill: "none", stroke: EDGE_PRIMARY_COLOR.GREY, strokeWidth: 1.5, className: "animated", d: edgePath })));
};
export const FlowCanvas = (props) => {
    const [canvasId] = useState((props === null || props === void 0 ? void 0 : props.id) || generateOFlowCanvasId());
    const model = useOFlowModel();
    const canvasDomRef = useRef(null);
    const [flowInstance, setFlowInstance] = useState();
    const { canvasModel, nodes, onNodesChange, canvasInstance, edges, setEdges } = useInitCanvasModel(Object.assign(Object.assign({}, props), { canvasDomRef,
        flowInstance, id: canvasId }));
    useBindEvents({ canvasModel, canvasDomRef, canvasInstance });
    const types = useTypes({ canvasId, canvasModel });
    const onEdgeUpdate = (oldEdge, newConnection) => setEdges(els => {
        const { target, source, targetHandle: targetPortId, sourceHandle: sourcePortId, } = newConnection;
        setTimeout(() => {
            canvasModel.command.execute('UPDATE_CONNECTION_OF_EDGE', {
                payload: {
                    id: oldEdge.id,
                    target,
                    source,
                    targetPortId,
                    sourcePortId,
                },
                changeDataOnly: true,
            });
        }, 20);
        return updateEdge(oldEdge, newConnection, els);
    });
    const { connectionRadius = 20 } = props;
    const editable = model.getEditable();
    return (React.createElement(CanvasRenderContainer, { canvasId: canvasId, ref: canvasDomRef },
        React.createElement(ReactFlow, Object.assign({ nodesDraggable: editable }, types, { connectOnClick: true, defaultEdgeOptions: { type: 'BaseEdge' }, onInit: setFlowInstance, nodes: nodes, edges: edges, onNodesChange: onNodesChange, onEdgeClick: (event, edge) => {
                const id = edge === null || edge === void 0 ? void 0 : edge.id;
                if (keyboardStatusMapping.ctrlKey || keyboardStatusMapping.metaKey) {
                    const mewSelectedIds = Array.from(new Set([...canvasModel.selectedElementsId, id]));
                    canvasModel.setSelectedElementIds(mewSelectedIds);
                }
                else {
                    canvasModel.setSelectedElementIds([id]);
                }
            }, onEdgeUpdate: onEdgeUpdate, onNodeClick: (event, node) => {
                const id = node === null || node === void 0 ? void 0 : node.id;
                if (keyboardStatusMapping.ctrlKey || keyboardStatusMapping.metaKey) {
                    const mewSelectedIds = Array.from(new Set([...canvasModel.selectedElementsId, id]));
                    canvasModel.setSelectedElementIds(mewSelectedIds);
                }
                else {
                    canvasModel.setSelectedElementIds([id]);
                }
            }, autoPanOnConnect: true, onConnect: (connection) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                if (editable) {
                    const { target, targetHandle, source, sourceHandle } = connection;
                    const edgeId = model.generateOFlowCanvasEdgeId();
                    yield ((_a = canvasModel === null || canvasModel === void 0 ? void 0 : canvasModel.command) === null || _a === void 0 ? void 0 : _a.execute('ADD_EDGE', {
                        changeDataOnly: true,
                        payload: { id: edgeId, target, source, targetHandle, sourceHandle },
                    }));
                    setEdges(previous => {
                        return [
                            ...previous,
                            dataTransForm.transformEdgeConfig2ReactFlowEdgeConfig({
                                id: edgeId,
                                target,
                                source,
                                targetHandle,
                                sourceHandle,
                            }),
                        ];
                    });
                }
            }), onClick: event => {
                var _a, _b, _c, _d;
                // @ts-ignore
                const isCanvas = (_c = (_b = (_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.className) === null || _b === void 0 ? void 0 : _b.includes) === null || _c === void 0 ? void 0 : _c.call(_b, 'react-flow__pane');
                if (isCanvas) {
                    (_d = canvasModel.setSelectedElementIds) === null || _d === void 0 ? void 0 : _d.call(canvasModel, []);
                }
            }, connectionLineComponent: defaultConnectionLine, connectionRadius: connectionRadius, deleteKeyCode: null }),
            React.createElement(Background, { color: "#aaa", gap: 16, style: { background: '#f5f5f5' } }))));
};
//# sourceMappingURL=index.js.map